import { ApolloQueryResult } from '@apollo/client';
import { EntityBase } from '@provision/entity-types';
import { stableStringify } from '@provision/formatting-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { uniqueWith } from 'remeda';
import { useNotifications } from 'src/utils/hooks';
import { QueryParams, Records } from './types';

interface UseLoadMoreProps<TQuery> {
  fetchMore: (data: {
    variables: {
      params: { offset: number; limit?: number };
      skipTotalRecords?: boolean;
    };
    updateQuery: (prevRecords: TQuery, next: { fetchMoreResult: TQuery }) => TQuery;
  }) => Promise<ApolloQueryResult<TQuery>>;
  params: QueryParams;
  offset: number;
  deduplicationPredicate?: <T extends EntityBase = EntityBase>(
    value1: T,
    value2: T,
  ) => boolean;
}

export const useLoadMore = <TQuery extends Record<string, Records<EntityBase>>>({
  fetchMore,
  params,
  offset,
  deduplicationPredicate,
}: UseLoadMoreProps<TQuery>) => {
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const loadMoreRef = useRef(false);
  const { showErrorAlert } = useNotifications();

  // since useLoadMore is a global utility hook that used everywhere - it is really important
  // to make sure params is stable before putting them into useEffect's dependency array
  const stableParamsDependency = stableStringify(params);

  useEffect(() => {
    // resetting paging flags for a fresh query
    loadMoreRef.current = false;
    setHasReachedEnd(false);
  }, [stableParamsDependency]);

  const loadMore = useCallback(
    async (localLimit?: number, localOffset?: number) => {
      if (hasReachedEnd || loadMoreRef.current) {
        return;
      }

      loadMoreRef.current = true;
      try {
        await fetchMore({
          // instead of replacing the entire "params" object,
          // merge the original query parameters with the new offset/limit.
          variables: {
            params: {
              ...params,
              offset: localOffset ?? offset,
              limit: localLimit ?? params.limit!,
            },
            // follow-up pages never need the count — it's already known from page 1
            skipTotalRecords: true,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            // no new data, end of list
            if (!fetchMoreResult) {
              setHasReachedEnd(true);
              return previousResult;
            }
            // results always contain single key as it is an instance of TQuery,
            // the key would be getConversationMessage or getConversation etc.
            // This allows us to avoid passing these strings explicitly
            const queryKey = Object.keys(previousResult)[0] as keyof TQuery;

            if (!queryKey) {
              return previousResult;
            }

            const prevRecords = previousResult[queryKey]?.records ?? [];
            const moreRecords = fetchMoreResult[queryKey]?.records ?? [];
            const requestedLimit = localLimit ?? params.limit;

            // first empty page, turn on hasReachedEnd and bail
            if (moreRecords.length === 0) {
              setHasReachedEnd(true);
              return previousResult;
            }

            /*
              A page the server could not fill is the last one, so callers do not have
              to spend another round trip discovering the end by asking for a page that
              comes back empty.
            */
            if (requestedLimit && moreRecords.length < requestedLimit) {
              setHasReachedEnd(true);
            }

            const records = prevRecords.concat(moreRecords);

            // otherwise merge and keep going
            return {
              ...previousResult,
              [queryKey]: {
                ...fetchMoreResult[queryKey],
                records: deduplicationPredicate
                  ? uniqueWith(records, deduplicationPredicate)
                  : records,
                // preserve page-1 totalRecords across follow-up merges:
                // fetchMoreResult.metadata.totalRecords is undefined when skipped,
                metadata: {
                  ...previousResult[queryKey]?.metadata,
                  ...fetchMoreResult[queryKey]?.metadata,
                },
              },
            };
          },
        });
      } catch (error) {
        showErrorAlert('Something went wrong');
        console.error(error);
      } finally {
        loadMoreRef.current = false;
      }
    },
    [hasReachedEnd, fetchMore, params, offset, deduplicationPredicate, showErrorAlert],
  );

  return useMemo(() => ({ loadMore, hasReachedEnd }), [loadMore, hasReachedEnd]);
};

/**
 * This structure is expected by Apollo to properly modify objects in cache.
 * The function accepts a "patch" object and creates a map of setters for each key in it,
 * returning new value.
 */
export type SettersMap<T> = { [K in keyof T]: (value: unknown) => T[K] };
export function generateFieldModifiersMap<T extends Record<string, unknown>>(patch: T) {
  return Object.keys(patch).reduce((acc, key) => {
    // Check required by Apollo typings for cache modifiers.
    if (typeof acc !== 'object' || acc === null) return acc;

    if (Object.hasOwn(patch, key)) {
      return {
        ...acc,
        [key]: (_oldValue: unknown) => {
          return patch[key];
        },
      };
    }
    return acc;
  }, {} as SettersMap<T>);
}
